.chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-s);
}

.stage {
  position: relative;

  width: 100%;
  max-width: 36rem;
  aspect-ratio: 4 / 3;
}

.wheel {
  display: block;

  width: 100%;
  height: 100%;

  overflow: visible;
}

.ring {
  fill: none;

  stroke: var(--border);
  stroke-width: 1;
}

.spoke {
  stroke: var(--border);
  stroke-width: 1;

  transition: stroke var(--duration-fast) var(--ease);
}

.spokeLit {
  stroke: var(--border-strong);
}

.outline {
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.marker {
  stroke: var(--surface);
  stroke-width: 1.25;
}

.axisLabel {
  font-family: var(--font-sans);
  font-size: 9px;
  font-weight: var(--weight-medium);

  fill: var(--text-muted);

  transition: fill var(--duration-fast) var(--ease);
}

.axisLabelLit {
  fill: var(--text);
}

/* An axis rated away: still on the wheel, because its scores are real, but no longer arguing. */
.axisLabelMuted {
  fill: var(--text-faint);
}

.spokeMuted {
  stroke-dasharray: 2 3;
}

/* The weight dot at the spoke's end, in the relevancy scale's own colours. */
.mark {
  fill: var(--mark, var(--text-faint));
  stroke: var(--surface-sunken);
  stroke-width: 1.5;
}

.muchLess {
  --mark: var(--relevancy-much-less);
}

.less {
  --mark: var(--relevancy-less);
}

.equal {
  --mark: var(--relevancy-equal);
}

.more {
  --mark: var(--relevancy-more);
}

.muchMore {
  --mark: var(--relevancy-much-more);
}

.tipMark {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);

  font-size: var(--size-100);

  color: var(--text-muted);
}

.tipMark::before {
  content: "";

  width: 0.5rem;
  height: 0.5rem;

  background-color: var(--mark, var(--text-faint));

  border-radius: var(--radius-pill);
}

.wedge {
  fill: transparent;

  cursor: default;
}

.tooltip {
  position: absolute;
  z-index: 1;

  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);

  min-width: 10rem;
  padding: var(--space-xs) var(--space-s);

  background-color: var(--surface-raised);

  border: 1px solid var(--border);
  border-radius: var(--radius-s);
  box-shadow: var(--shadow-raised);

  pointer-events: none;
}

/*
 * The tooltip leans away from the side of the wheel its axis sits on, so it
 * never covers the shape a reader is looking at.
 */
.tipstart {
  transform: translate(var(--space-xs), -50%);
}

.tipend {
  transform: translate(calc(-100% - var(--space-xs)), -50%);
}

.tipmiddle {
  transform: translate(-50%, -50%);
}

.tipTitle {
  font-size: var(--size-200);
  font-weight: var(--weight-strong);

  color: var(--text);
}

.tipRow {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);

  font-size: var(--size-200);

  color: var(--text-muted);
}

.tipName {
  flex: 1;
}

.tipValue {
  font-variant-numeric: tabular-nums;
  font-weight: var(--weight-medium);

  color: var(--text);
}

.swatch {
  flex: none;

  width: 0.625rem;
  height: 0.625rem;

  border-radius: var(--radius-pill);
}

.square {
  border-radius: var(--radius-xs);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-m);

  list-style: none;
}

.legendItem {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2xs) var(--space-xs);

  font-size: var(--size-200);

  color: var(--text-muted);
}

.empty {
  padding: var(--space-xl) 0;

  font-size: var(--size-200);

  color: var(--text-muted);
}

/*
 * The same numbers as the wheel, in the order a screen reader can walk. Taken
 * out of the flow rather than hidden, because `display: none` would take it out
 * of the accessibility tree along with it.
 */
.readout {
  position: absolute;

  width: 1px;
  height: 1px;
  overflow: hidden;

  clip-path: inset(50%);

  white-space: nowrap;
}
